/**
 * LeadDraftCard — wrapper que conecta hooks de leads al DraftCard shared.
 * Sprint 7.6 Bloque A.
 */
import { useTranslation } from 'react-i18next';
import { DraftCard } from '../outreach/DraftCard.js';
import type { DraftCardLabels, NormalizedDraft } from '../outreach/types.js';
import {
  useSendDraft,
  useCancelDraft,
  useSkipDraft,
  useUpdateDraft,
  useSendLeadEmail,
  type OutreachDraft,
} from '../../hooks/useLeads.js';

interface LeadDraftCardProps {
  draft: OutreachDraft;
  leadId: string;
  isObsolete?: boolean;
}

export function LeadDraftCard({ draft, leadId, isObsolete = false }: LeadDraftCardProps) {
  const { t } = useTranslation();
  const sendDraft = useSendDraft();
  const cancelDraft = useCancelDraft();
  const skipDraft = useSkipDraft();
  const updateDraft = useUpdateDraft(leadId);
  const sendLeadEmail = useSendLeadEmail(leadId);

  const normalized: NormalizedDraft = {
    id: draft.id,
    draftType: draft.draftType,
    subject: draft.subject,
    bodyHtml: draft.bodyHtml,
    bodyText: draft.bodyText,
    status: draft.status === 'approved' || draft.status === 'paused' ? 'pending_review' : draft.status,
    sendError: draft.sendError,
    sentAt: draft.sentAt,
    createdAt: draft.createdAt,
    sequenceOrder: draft.sequenceOrder,
    scheduledFor: draft.scheduledFor,
    isDue: draft.isDue,
    sentMessageId: draft.sentMessageId,
    generatedByClaude: draft.generatedByClaude,
  };

  const labels: DraftCardLabels = {
    initial: t('leads.outreach.draftType.initial'),
    followupN: t('leads.outreach.draftType.followup', { n: '{n}' }),
    newEmail: t('leads.outreach.draftType.initial'),
    briefRequest: '',
    contentPlanReview: '',
    designReview: '',
    replyType: '',
    editBtn: t('leads.outreach.editBtn'),
    editSave: t('leads.outreach.editSave'),
    editCancel: t('leads.outreach.editCancel'),
    sendBtn: t('leads.outreach.sendBtn'),
    sendingLabel: t('leads.outreach.sending'),
    sendNow: t('leads.outreach.send_now'),
    cancelBtn: t('leads.outreach.cancel'),
    skipBtn: t('leads.outreach.skip'),
    retryBtn: t('outreach.actions.retrySend'),
    expandBtn: t('leads.outreach.expandBtn'),
    collapseBtn: t('leads.outreach.collapseBtn'),
    copyBtn: t('leads.outreach.copyBtn'),
    writtenByHand: t('leads.outreach.writtenByHand'),
    copied: t('leads.outreach.copied'),
    replyBtn: t('leads.outreach.replyBtn'),
    sendReplyBtn: t('leads.outreach.sendReplyBtn'),
    sentAgo: t('leads.outreach.sent_ago', { time: '{time}' }),
    scheduledForLabel: t('leads.outreach.scheduled_for', { date: '{date}' }),
    dueNow: t('leads.outreach.due_now'),
    readyBanner: t('leads.outreach.ready_to_send_banner'),
    scheduledWasLabel: t('leads.outreach.scheduled_was', { time: '{time}' }),
    followupObsolete: t('leads.outreach.followupObsolete'),
    willSchedule: t('leads.outreach.will_schedule'),
    sentBodyNote: t('leads.outreach.sentBodyNote'),
    failedStatus: t('outreach.status.failed'),
    cancelledStatus: 'CANCELLED',
    skippedStatus: 'SKIPPED',
    editSubjectLabel: t('leads.outreach.editSubjectLabel'),
    editBodyLabel: t('leads.outreach.editBodyLabel'),
    actionFailed: t('leads.outreach.action_failed'),
    composerPlaceholder: t('composer.editor.placeholder'),
  };

  return (
    <DraftCard
      draft={normalized}
      labels={labels}
      useRichTextEditor
      isObsolete={isObsolete}
      onSend={() => sendDraft.mutate({ leadId, draftId: draft.id })}
      onCancel={() => cancelDraft.mutate({ leadId, draftId: draft.id })}
      onSkip={() => skipDraft.mutate({ leadId, draftId: draft.id })}
      onUpdate={({ subject, bodyHtml, bodyText }) =>
        updateDraft.mutate({ draftId: draft.id, subject, body_html: bodyHtml, body_text: bodyText })
      }
      onReply={({ subject, bodyHtml, bodyText, inReplyTo, references }) =>
        sendLeadEmail.mutate({
          subject,
          bodyHtml,
          bodyText,
          ...(inReplyTo ? { inReplyTo, ...(references ? { references } : {}) } : {}),
        })
      }
      sendState={{ isPending: sendDraft.isPending, isError: sendDraft.isError, error: sendDraft.error }}
      cancelState={{ isPending: cancelDraft.isPending, isError: cancelDraft.isError, error: cancelDraft.error }}
      skipState={{ isPending: skipDraft.isPending, isError: skipDraft.isError, error: skipDraft.error }}
      updateState={{ isPending: updateDraft.isPending, isError: updateDraft.isError, error: updateDraft.error }}
      replyState={{ isPending: sendLeadEmail.isPending, isError: sendLeadEmail.isError, error: sendLeadEmail.error, reset: sendLeadEmail.reset }}
    />
  );
}
